import React, { useState, useEffect, useRef } from 'react'
import { apiHandler } from '../../api/apihandler';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { Link, useLocation, useNavigate, Outlet, Navigate } from 'react-router-dom'
import { NOTIFICATION_COUNT, GENERAL_SETTINGS, AI_CHAT_START, TEACHER_MODULE_PERMISSIONS } from "../../api/constants";
import { io } from "socket.io-client";
import Header from '../../components/partials/dashboard/HeaderStyle/header'
import Sidebar from '../../components/partials/dashboard/SidebarStyle/sidebar'
import {
    canTeacherAccessPath,
    clearStoredTeacherModulePermissionData,
    getStoredTeacherModulePermissionData,
    getTeacherPermissionFallbackPath,
    isTeacherLoginProfile,
    setStoredTeacherModulePermissionData,
} from '../../utilities/teacherModulePermissions';

// Redux Selector / Action
import { useDispatch, useSelector } from 'react-redux';
import { setIsOptimizing, setGeneralSettings as setGeneralSettingsRedux } from '../../store/setting/actions'
//default 
// import DefaultRouter from '../../router/default-router'

// share-offcanvas
// import ShareOffcanvas from '../../components/share-offcanvas'

//settingoffCanvas
import SettingOffCanvas from '../../components/setting/SettingOffCanvas'
import DefaultHeader from '../../components/partials/dashboard/HeaderStyle/defaultHeader';

const Default = () => {
    const dispatch = useDispatch()
    let location = useLocation();
    const navigate = useNavigate();
    const socketRef = useRef(null);
    let profileData = (useSelector((state) => state.setting.setting.user_data))
    profileData = profileData ? JSON.parse(profileData) : "";

    const [notificationCount, setNotificationCount] = useState(0);
    const [generalSettings, setGeneralSettings] = useState('');
    const [teacherPermissionData, setTeacherPermissionData] = useState(getStoredTeacherModulePermissionData());
    const [teacherPermissionsLoaded, setTeacherPermissionsLoaded] = useState(false);

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [showAIChatModal, setShowAIChatModal] = useState(false);

    const authToken = localStorage.getItem("uzoneAuthToken");
    // '/' and '/landing' are always chromeless (marketing pages) regardless
    // of auth state. The static info pages are also reachable signed-out,
    // but once signed in they should look like any other dashboard screen
    // (sidebar + no header, same as /pages/home) rather than staying
    // chromeless - so they're tracked separately from isPublicPath, which
    // only suppresses the sidebar/header.
    const publicExactPaths = [
        '/',
        '/landing',
    ];
    const staticInfoPaths = [
        '/privacy-policy',
        '/about-us',
        '/contact-us',
        '/terms-of-use',
    ];
    const isPublicPath = publicExactPaths.includes(location.pathname);
    const isStaticInfoPath = staticInfoPaths.includes(location.pathname);

    useEffect(() => {
        socketRef.current = io(process.env.REACT_APP_API_BASE_URL);

        return () => {
            if (socketRef.current) {
                socketRef.current.removeAllListeners();
                socketRef.current.disconnect();
                socketRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        getGeneralSettings()
        if (localStorage.getItem("uzoneAuthToken")) {
            getNotificationCount()
            socketLogedInSetup()
            loadTeacherModulePermissions()
            if ((profileData?.user_setting?.is_influencer != 1 || profileData?.user_setting?.is_dance_corner != 1 ||
                profileData?.user_type != 2 || profileData?.user_type != 4
            ) && profileData?.user_setting?.user_mood > 3
            ) {
                getAIChatStartApi()
            }
        } else {
            clearStoredTeacherModulePermissionData()
            setTeacherPermissionData(null)
            setTeacherPermissionsLoaded(false)
        }

        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // The generic right sidebar (AI chat widgets) has been removed everywhere,
    // so main-content never needs to reserve space for it.
    useEffect(() => {
        document.body.classList.add('no-right-sidebar');
        return () => document.body.classList.remove('no-right-sidebar');
    }, []);

    // Header is hidden on authenticated dashboard pages (Sidebar carries the
    // brand instead) - let CSS know so the fixed sidebar can sit flush at top:0
    // instead of leaving the gap it normally reserves for the header.
    useEffect(() => {
        const headerHidden = !!localStorage.getItem('uzoneAuthToken') && !isPublicPath;
        if (headerHidden) {
            document.body.classList.add('no-header');
        } else {
            document.body.classList.remove('no-header');
        }
        return () => document.body.classList.remove('no-header');
    }, [isPublicPath]);

    // The nemonto-branded screens (spice/cream/herb palette) still render
    // inside the shared Sidebar, which defaults to Bootstrap's indigo theme -
    // flag those routes so nemonto-theme-overrides.css can re-point the
    // Sidebar (and any Modal/Dropdown they open) at the nemonto palette.
    useEffect(() => {
        const nemontoThemedPaths = [
            '/dashboard/app/profile',
            '/dashboard/app/user-profile-edit',
            '/notifications',
            '/people-you-may-know',
            '/followers',
            '/following',
            '/blocked-accounts',
            '/groups',
            '/favourite-recipes',
            '/dashboards/app/my-ads',
            '/privacy-policy',
            '/about-us',
            '/contact-us',
            '/terms-of-use',
        ];
        const isNemontoThemedPage = nemontoThemedPaths.includes(location.pathname)
            || location.pathname === '/pages'
            || location.pathname.startsWith('/pages/');
        if (isNemontoThemedPage) {
            document.body.classList.add('nemonto-theme');
        } else {
            document.body.classList.remove('nemonto-theme');
        }
        return () => document.body.classList.remove('nemonto-theme');
    }, [location.pathname]);

    // Sidebar navigation swaps the routed <Outlet/> content in place - the
    // window/document scroll position isn't reset by React Router, so a new
    // screen can render mid-scroll if the previous one was scrolled down.
    // Reset to the top on every route change. This only touches window/
    // document scroll, not the nemonto home feed's own internal
    // .nmha-feed-col scroll container (which intentionally restores its own
    // position when navigating back to it).
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    useEffect(() => {
        if (!localStorage.getItem("uzoneAuthToken")) {
            return;
        }

        if (!profileData?.id) {
            return;
        }

        loadTeacherModulePermissions();
    }, [profileData?.id]);

    useEffect(() => {
        if (!localStorage.getItem("uzoneAuthToken")) {
            return;
        }

        if (!isTeacherLoginProfile(profileData)) {
            return;
        }

        if (!teacherPermissionsLoaded) {
            return;
        }

        const canAccess = canTeacherAccessPath(location.pathname, profileData, teacherPermissionData);
        if (!canAccess) {
            const fallbackPath = getTeacherPermissionFallbackPath(teacherPermissionData);
            if (fallbackPath && fallbackPath !== location.pathname) {
                navigate(fallbackPath, { replace: true });
            }
        }
    }, [location.pathname, navigate, profileData, teacherPermissionData, teacherPermissionsLoaded]);

    useEffect(() => {
        const handleNotificationCountRefresh = () => {
            if (localStorage.getItem("uzoneAuthToken")) {
                getNotificationCount();
            }
        };

        window.addEventListener('notification-count-refresh', handleNotificationCountRefresh);

        return () => {
            window.removeEventListener('notification-count-refresh', handleNotificationCountRefresh);
        };
    }, []);

    function socketLogedInSetup() {
        const socket = socketRef.current;
        if (!socket) {
            return;
        }

        const userId = profileData?.id;
        socket.removeAllListeners('connect');
        socket.removeAllListeners('disconnect');
        socket.removeAllListeners('error');
        socket.removeAllListeners('notificationUpdate');
        socket.removeAllListeners('logedinusers');
        socket.removeAllListeners('userlogedout');
        socket.removeAllListeners('mediaOptimized');

        // Listen for events from the server
        socket.on('connect', () => {
            // console.log('Connected to the server');
            socket.emit("login", userId);
        });

        // Listen for events from the server
        socket.on('disconnect', () => {
            socket.emit("logedout", userId);
            // console.log('DisConnected from the server');
        });
        socket.on('error', () => {
            console.log('DisConnected from the server');
        });

        socket.on('notificationUpdate', (message) => {
            //console.log('logedin users list:', message);
            setNotificationCount(message)
            // console.log('notification Count socket:', message);
        });
        socket.on('mediaOptimized', (message) => {
            console.log('optimized:', message);
            dispatch(setIsOptimizing(true));
        });
    }

    async function getGeneralSettings() {
        try {
            const res = await apiHandler(GENERAL_SETTINGS, "POST", {
            })
            if (res.data?.response?.status === 200) {
                setGeneralSettings(res?.data?.data?.generalSettings)
                dispatch(setGeneralSettingsRedux(JSON.stringify(res?.data?.data?.generalSettings)));
            }
        } catch (error) {
            console.error(error);
        }
    }

    async function getNotificationCount() {
        try {
            const res = await apiHandler(NOTIFICATION_COUNT, "POST")
            if (res.data?.response?.status === 200) {
                setNotificationCount(res?.data?.data?.count)
                //console.log('notification Count:', res?.data?.data?.count);
            } else {
                //addToast(res?.response?.data?.data?.message, { appearance: 'error' });
            }
        } catch (error) {
            console.error(error);
        } finally {
        }
    }

    async function getAIChatStartApi() {
        try {
            const res = await apiHandler(AI_CHAT_START, 'POST');
            if (res.data?.response?.status === 200) {
                setShowAIChatModal(true);
            } else {
            }
        } catch (error) {
            console.error(error);
        } finally {
        }
    }

    async function loadTeacherModulePermissions() {
        setTeacherPermissionsLoaded(false);
        try {
            if (!isTeacherLoginProfile(profileData)) {
                clearStoredTeacherModulePermissionData();
                setTeacherPermissionData(null);
                return;
            }

            const res = await apiHandler(TEACHER_MODULE_PERMISSIONS, 'GET');
            if (res?.data?.response?.status === 200 && res?.data?.data) {
                setStoredTeacherModulePermissionData(res.data.data);
                setTeacherPermissionData(getStoredTeacherModulePermissionData());
            } else {
                clearStoredTeacherModulePermissionData();
                setTeacherPermissionData(null);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setTeacherPermissionsLoaded(true);
        }
    }

    if (!authToken && !isPublicPath && !isStaticInfoPath) {
        return <Navigate to="/auth/sign-in" replace />;
    }

    const showSidebar = !!localStorage.getItem("uzoneAuthToken") && !isPublicPath;

    const toggleSidebar = () => {
        const sidebar = document.getElementsByTagName("ASIDE")[0];
        if (!sidebar) return;
        if (window.innerWidth < 1200) {
            sidebar.classList.toggle("sidebar-open");
        } else {
            sidebar.classList.toggle("sidebar-mini");
        }
    };

    return (
        <>
            {showSidebar ? <Sidebar notificationCount={notificationCount} /> : null}
            {showSidebar ? (
                <button type="button" className="nvn-menu-toggle" onClick={toggleSidebar} aria-label="Toggle menu">
                    <span className="material-symbols-outlined">menu</span>
                </button>
            ) : null}
            {
                localStorage.getItem("uzoneAuthToken") ?
                    location.pathname === '/' ?
                        <Header notificationCount={notificationCount} /> : null
                    : <Header notificationCount={notificationCount} />
            }
            <div className="main-content">
                {/* <div id="content-page" className="content-page"> */}
                {/* <DefaultRouter/> */}
                <Outlet />
                {/* </div> */}
            </div>
            <Modal
                show={showAIChatModal}
                onHide={() => setShowAIChatModal(false)}
                centered
                backdrop="static"
                keyboard={false}
            >
                <Modal.Body className="text-center p-4" style={{ borderRadius: 16 }}>
                    <img
                        src="https://cdn-icons-png.flaticon.com/512/4712/4712035.png"
                        alt="AI Friend"
                        style={{ width: 80, marginBottom: 16 }}
                    />
                    <h4 style={{ fontWeight: 700, color: "#1976d2" }}>You have one friend waiting to talk to you.</h4>
                    <p style={{ color: "#555", marginTop: 12, marginBottom: 24 }}>
                        Would you like to start a conversation with your AI Counsellor now?
                    </p>
                    <div className="d-flex justify-content-center gap-3">
                        <Button
                            variant="primary"
                            style={{ minWidth: 100, fontWeight: 600 }}
                            onClick={() => {
                                setShowAIChatModal(false);
                                window.open(`/chat/index?type=user&slug=${"ai-chat"}`, '_blank');
                            }}
                        >
                            Yes
                        </Button>
                        <Button
                            variant="outline-secondary"
                            style={{ minWidth: 100, fontWeight: 600 }}
                            onClick={() => setShowAIChatModal(false)}
                        >
                            Not Now
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
            {/* <SettingOffCanvas /> */}
        </>
    )
}

export default Default
